import React, { useState } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { mediaQuery } from "@zendeskgarden/react-theming"
import { Accordion } from "@zendeskgarden/react-accordions"
import { Paragraph } from "@zendeskgarden/react-typography"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import pick from "lodash/pick"
import { Media } from "../../lib/media-queries"
import { H2, Subheading } from "../../styles/typography"
import { gatsbyImageDataShape } from "../../lib/prop-types"
import ArrowLink from "./arrowLink"

const Container = styled.div`
  display: flex;
  align-items: stretch;

  ${props => mediaQuery("down", "xs", props.theme)} {
    flex-direction: column;
  }
`

const StyledMedia = styled(Media)`
  display: flex;
`

const AccordionContainer = styled.div`
  flex-basis: ${props => (props.$imagesPosition ? "55%" : "100%")};
  flex-shrink: 0;
  ${props =>
    props.$imagesPosition === "left" &&
    `
    order: 1;
  `}

  ${props => mediaQuery("down", "xs", props.theme)} {
    order: 1;
  }
`

// TODO: find out where the 20px margin-bottom is coming from so we don't need to overwrite it with !important
const StyledAccordionSection = styled(Accordion.Section)`
  margin-bottom: ${props =>
    props.$selected ? props.theme.space.sm : "inherit !important"};
  box-shadow: ${props =>
    props.$selected &&
    props.theme.shadows.lg(
      0,
      `${props.theme.space.base * 4}px`,
      props.theme.colors.header.shadow
    )};
`

const StyledAccordionLabel = styled(Accordion.Label)`
  font-weight: ${props => props.theme.fontWeights.light};
  font-size: ${props => props.theme.fontSizes.lg};
  line-height: ${props => props.theme.lineHeights.lg};
`

const LabelSecondary = styled(Paragraph)`
  color: ${props => props.theme.colors.document.subheading};
`

const Description = styled(Paragraph)`
  margin-bottom: ${props => props.theme.space.sm};
`

const ImagesContainer = ({ className, sectionImages, expandedSection }) => (
  <div
    className={className}
    data-testid="dev-docs-feature-accordion-images-container"
  >
    {sectionImages.map((sectionImage, index) => {
      const image = getImage(sectionImage.imageData)
      return (
        <ImageClamp key={image.images.fallback.src}>
          <StyledGatsbyImage
            image={image}
            alt={sectionImage.imageAltText}
            // the CSS properties below appear to require being passed in via
            // the imgStyle prop, as they aren't respected when declared
            // in the styled-component
            imgStyle={{
              objectFit: "contain",
              maxHeight: "80%",
              margin: "0 auto",
              top: "10%"
            }}
            $hidden={index !== expandedSection}
          />
        </ImageClamp>
      )
    })}
  </div>
)

ImagesContainer.propTypes = {
  className: PropTypes.string,
  sectionImages: PropTypes.arrayOf(
    PropTypes.shape({
      imageData: gatsbyImageDataShape,
      imageAltText: PropTypes.string
    })
  ).isRequired,
  expandedSection: PropTypes.number.isRequired
}

ImagesContainer.defaultProps = {
  className: null
}

const StyledImagesContainer = styled(ImagesContainer)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.space.md};
  background-color: ${props => props.backgroundColor};
  ${props =>
    props.$position === "left" &&
    `
    order: 0;
    margin-right: ${props.theme.space.lg};
  `}

  ${props =>
    props.$position === "right" &&
    `
    order: 1;
    margin-left: ${props.theme.space.lg};
  `}

  ${props => mediaQuery("down", "xs", props.theme)} {
    order: 0;
    width: auto;
    margin: 0 auto ${props => props.theme.space.xl};
  }
`

const ImageClamp = styled.div`
  max-width: 80%;

  ${props => mediaQuery("down", "xs", props.theme)} {
    height: ${props => props.theme.space.base * 64}px;
  }
`

const StyledGatsbyImage = styled(GatsbyImage)`
  display: ${props => props.$hidden && "none !important"};
  height: 100%;
`

const FeatureAccordion = ({
  className,
  inlineHeading,
  inlineSubheading,
  sections,
  showImages,
  imageBackgroundColor,
  level
}) => {
  const [expandedSection, setExpandedSection] = useState(0)

  const accordionSections = sections.map((section, index) => {
    const { label, labelSecondary, description, linkText, linkHref } = section
    return (
      <StyledAccordionSection
        key={label}
        className={className}
        $selected={index === expandedSection}
      >
        <Accordion.Header>
          <StyledAccordionLabel>{label}</StyledAccordionLabel>
        </Accordion.Header>
        <Accordion.Panel>
          {labelSecondary && <LabelSecondary>{labelSecondary}</LabelSecondary>}
          <Description>{description}</Description>
          <ArrowLink href={linkHref} isFocusable={index === expandedSection}>
            {linkText}
          </ArrowLink>
        </Accordion.Panel>
      </StyledAccordionSection>
    )
  })

  const imagesContainer = (
    <StyledImagesContainer
      className={className}
      $position={showImages}
      sectionImages={sections.map(section =>
        pick(section, ["imageData", "imageAltText"])
      )}
      expandedSection={expandedSection}
      backgroundColor={imageBackgroundColor}
    />
  )

  const showImagesContainer = showImages && sections[expandedSection].imageData

  return (
    <Container>
      <AccordionContainer
        $imagesPosition={showImages}
        data-testid="dev-docs-feature-accordion-accordion-container"
      >
        {inlineHeading && <H2 bigger>{inlineHeading}</H2>}
        {inlineSubheading && <Subheading>{inlineSubheading}</Subheading>}
        <StyledMedia lessThan="sm">
          {showImagesContainer && imagesContainer}
        </StyledMedia>
        <Accordion
          level={level}
          expandedSections={[expandedSection]}
          onChange={index => setExpandedSection(index)}
        >
          {accordionSections}
        </Accordion>
      </AccordionContainer>
      <StyledMedia greaterThanOrEqual="sm">
        {showImagesContainer && imagesContainer}
      </StyledMedia>
    </Container>
  )
}

FeatureAccordion.propTypes = {
  className: PropTypes.string,
  inlineHeading: PropTypes.string,
  inlineSubheading: PropTypes.string,
  showImages: PropTypes.oneOf(["left", "right", null]),
  imageBackgroundColor: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      labelSecondary: PropTypes.string,
      subheading: PropTypes.string,
      description: PropTypes.string.isRequired,
      linkText: PropTypes.string.isRequired,
      linkHref: PropTypes.string.isRequired,
      imageData: gatsbyImageDataShape,
      imageAltText: PropTypes.string
    })
  ).isRequired,
  level: PropTypes.number
}

FeatureAccordion.defaultProps = {
  className: null,
  inlineHeading: null,
  inlineSubheading: null,
  showImages: null,
  imageBackgroundColor: null,
  level: 4
}

export default FeatureAccordion
